import { RowSelectionState, SortingState } from '@tanstack/react-table';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useState,
} from 'react';

export type Pagination = {
  pageIndex: number;
  pageSize: number;
};

export type Filters = {
  locationIds: string[];
  positionIds: string[];
  isSelectedForSourcing?: boolean;
  brandIds: string[];
  active: boolean | undefined;
};

export type TableContextType = {
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  rowCount: number;
  setRowCount: Dispatch<SetStateAction<number>>;
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  searchQuery: string | undefined;
  setSearchQuery: Dispatch<SetStateAction<string | undefined>>;
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
};

export const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
export const DEFAULT_ROWS_PER_PAGE = ROWS_PER_PAGE_OPTIONS[0];

const defaultPagination = {
  pageIndex: 0,
  pageSize: DEFAULT_ROWS_PER_PAGE,
};

export const TableContext = createContext<TableContextType>({
  pagination: defaultPagination,
  setPagination: () => void 0,
  rowCount: 0,
  setRowCount: () => void 0,
  rowSelection: {},
  setRowSelection: () => void 0,
  searchQuery: undefined,
  setSearchQuery: () => void 0,
  filters: {
    locationIds: [],
    positionIds: [],
    isSelectedForSourcing: false,
    brandIds: [],
    active: undefined,
  },
  setFilters: () => void 0,
  sorting: [],
  setSorting: () => void 0,
});

export function useTableContext() {
  const context = useContext(TableContext);

  if (!context)
    throw new Error('useTableContext must be used within a TableProvider');

  return context;
}

export function TableProvider({ children }: PropsWithChildren) {
  const [pagination, setPagination] = useState(defaultPagination);
  const [rowCount, setRowCount] = useState(0);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchQuery, setSearchQuery] = useState<string>();
  const [filters, setFilters] = useState<Filters>({
    locationIds: [],
    positionIds: [],
    isSelectedForSourcing: false,
    brandIds: [],
    active: undefined,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  return (
    <TableContext.Provider
      value={{
        pagination,
        setPagination,
        rowCount,
        setRowCount,
        rowSelection,
        setRowSelection,
        searchQuery,
        setSearchQuery,
        filters,
        setFilters,
        sorting,
        setSorting,
      }}
    >
      {children}
    </TableContext.Provider>
  );
}
